import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  Chip, 
  Tabs, 
  Tab, 
  CircularProgress, 
  Alert,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Badge,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { 
  Search, 
  FilterList, 
  Image as ImageIcon, 
  DeviceHub, 
  LocalFireDepartment, 
  LocalOffer,
  CalendarToday,
  Store,
  Public,
  Refresh
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './constants';
import { mergeJtiProductRowOverrides } from './productUtils';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { getDefaultCountryForProject, getDefaultManufacturerRetailers } from './utils/brandBoardUtils';

// Brand Board component - evaluation and comparison of brand activities
function BrandBoardView() {
  const { projectKey } = useParams();
  
  // State for data
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for segmentation tabs
  const [activeTab, setActiveTab] = useState(2); // Default to Sticks tab
  const tabLabels = ['All', 'Devices', 'Sticks', 'Promo Offers'];
  
  // State for filters
  const [selectedCountry, setSelectedCountry] = useState('ro'); // Default to Romania
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: dayjs().subtract(30, 'days'),
    end: dayjs()
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPromoTypes, setSelectedPromoTypes] = useState([]);
  
  // State for variant and intensity filters (for Sticks)
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [selectedIntensities, setSelectedIntensities] = useState([]);
  
  // State for currency conversion
  const [convertToEUR, setConvertToEUR] = useState(true); // Default to EUR conversion
  
  // State for unique filter options
  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    retailers: [],
    brands: [],
    categories: [],
    promoTypes: [],
    variants: [],
    intensities: []
  });
  
  // State for image preview
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  
  // State for product detail dialog
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetailDialogOpen, setProductDetailDialogOpen] = useState(false);

  const defaultManufacturerRetailersAppliedRef = useRef(false);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (defaultManufacturerRetailersAppliedRef.current || selectedRetailers.length > 0) return;
    const r = filterOptions.retailers || [];
    if (!r.length) return;
    const mfr = getDefaultManufacturerRetailers(r);
    defaultManufacturerRetailersAppliedRef.current = true;
    if (mfr.length > 0) setSelectedRetailers(mfr);
  }, [filterOptions.retailers, selectedRetailers.length]);

  useEffect(() => {
    let rows = data;
    if (selectedCategories.length > 0) {
      rows = rows.filter((item) => selectedCategories.includes(item.clean_category));
    }
    const brands = [...new Set(rows.map((item) => item.brand).filter(Boolean))].sort();
    setFilterOptions((prev) => ({ ...prev, brands }));
  }, [data, selectedCategories]);

  useEffect(() => {
    const allowed = new Set(filterOptions?.brands || []);
    if (!allowed.size) return;
    setSelectedBrands((prev) => {
      const next = prev.filter((b) => allowed.has(b));
      return next.length === prev.length ? prev : next;
    });
  }, [filterOptions.brands]);
  
  // Apply filters whenever filter state changes
  useEffect(() => {
    applyFilters();
  }, [data, selectedCountry, selectedRetailers, selectedBrands, selectedCategories, dateRange, searchTerm, selectedPromoTypes, selectedVariants, selectedIntensities, activeTab]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const [dataResponse, overridesResponse, brandOverridesResponse] = await Promise.all([
        axios.get(`${API_URL}/jti/global-merged-data`),
        axios.get(`${API_URL}/jti/product-category-overrides`).catch(() => ({ data: { overrides: {} } })),
        axios.get(`${API_URL}/jti/product-brand-overrides`).catch(() => ({ data: { overrides: {} } })),
      ]);
      
      if (dataResponse.data && dataResponse.data.data) {
        const rawData = dataResponse.data.data;
        const columns = dataResponse.data.columns;
        
        // Convert array of arrays to array of objects
        const formattedData = rawData.map(row => {
          const obj = {};
          columns.forEach((col, index) => {
            obj[col] = row[index];
          });
          return obj;
        });

        const overrides = overridesResponse?.data?.overrides || {};
        const brandOverrides = brandOverridesResponse?.data?.overrides || {};
        const formattedWithOverrides = formattedData.map((item) =>
          mergeJtiProductRowOverrides(item, overrides, brandOverrides)
        );
        
        setData(formattedWithOverrides);
        
        // Extract unique filter options
        extractFilterOptions(formattedWithOverrides);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load brand board data');
    } finally {
      setLoading(false);
    }
  };
  
  const extractFilterOptions = (data) => {
    const countries = [...new Set(data.map(item => item.country).filter(Boolean))];
    const retailers = [...new Set(data.map(item => item.retailer).filter(Boolean))];
    const brands = [...new Set(data.map(item => item.brand).filter(Boolean))];
    const categories = [...new Set(data.map(item => item.clean_category).filter(Boolean))];
    const variants = [...new Set(data.map(item => item.variant).filter(Boolean))];
    const intensities = [...new Set(data.map(item => item.intensity).filter(Boolean))];
    
    // Extract promo types from title/description analysis
    const promoTypes = [...new Set(
      data
        .map(item => item.title || '')
        .filter(title => title.toLowerCase().includes('offer') || 
                        title.toLowerCase().includes('promo') || 
                        title.toLowerCase().includes('bundle') ||
                        title.toLowerCase().includes('kit'))
        .map(title => {
          if (title.toLowerCase().includes('bundle')) return 'Bundle';
          if (title.toLowerCase().includes('kit')) return 'Starter Kit';
          if (title.toLowerCase().includes('offer')) return 'Special Offer';
          return 'Promotion';
        })
    )];
    
    setFilterOptions({
      countries,
      retailers,
      brands,
      categories,
      promoTypes,
      variants,
      intensities
    });
    const defaultCountry = getDefaultCountryForProject(projectKey, countries);
    if (defaultCountry) {
      setSelectedCountry(defaultCountry);
    } else if (countries.length > 0 && !countries.includes(selectedCountry)) {
      setSelectedCountry(countries[0]);
    }
  };
  
  const applyFilters = () => {
    let filtered = [...data];
    
    // Filter by country (default to Romania)
    if (selectedCountry) {
      filtered = filtered.filter(item => item.country === selectedCountry);
    }
    
    // Filter by retailers
    if (selectedRetailers.length > 0) {
      filtered = filtered.filter(item => selectedRetailers.includes(item.retailer));
    }
    
    // Filter by brands
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(item => selectedBrands.includes(item.brand));
    }
    
    // Filter by categories
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(item => selectedCategories.includes(item.clean_category));
    }
    
    // Filter by date range
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter(item => {
        const itemDate = dayjs(item.updated_at);
        return itemDate.isAfter(dateRange.start.subtract(1, 'day')) && 
               itemDate.isBefore(dateRange.end.add(1, 'day'));
      });
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => 
        (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.brand && item.brand.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filter by promo types
    if (selectedPromoTypes.length > 0) {
      filtered = filtered.filter(item => {
        const title = (item.title || '').toLowerCase();
        return selectedPromoTypes.some(promoType => {
          switch (promoType) {
            case 'Bundle':
              return title.includes('bundle');
            case 'Starter Kit':
              return title.includes('kit');
            case 'Special Offer':
              return title.includes('offer');
            case 'Promotion':
              return title.includes('promo');
            default:
              return false;
          }
        });
      });
    }
    
    // Filter by variants (for Sticks)
    if (selectedVariants.length > 0) {
      filtered = filtered.filter(item => selectedVariants.includes(item.variant));
    }
    
    // Filter by intensities (for Sticks)
    if (selectedIntensities.length > 0) {
      filtered = filtered.filter(item => selectedIntensities.includes(item.intensity));
    }
    
    // Apply segmentation based on active tab
    filtered = applySegmentation(filtered, activeTab);
    
    setFilteredData(filtered);
  };
  
  const applySegmentation = (data, tabIndex) => {
    switch (tabIndex) {
      case 0: // All
        return data; // Return all data without filtering
      case 1: // Devices
        return data.filter(item => 
          item.clean_category === 'Devices' || 
          (item.title && item.title.toLowerCase().includes('device'))
        );
      case 2: // Sticks
        return data.filter(item => 
          item.clean_category === 'Consumables' || 
          (item.title && (
            item.title.toLowerCase().includes('stick') ||
            item.title.toLowerCase().includes('terea') ||
            item.title.toLowerCase().includes('levia') ||
            item.title.toLowerCase().includes('delia') ||
            item.title.toLowerCase().includes('fiit')
          ))
        );
      case 3: // Promo Offers
        return data.filter(item => 
          item.clean_category === 'Offers' || 
          item.clean_category === 'Starter Kits' ||
          (item.title && (
            item.title.toLowerCase().includes('offer') ||
            item.title.toLowerCase().includes('promo') ||
            item.title.toLowerCase().includes('bundle') ||
            item.title.toLowerCase().includes('kit')
          ))
        );
      default:
        return data;
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };
  
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setProductDetailDialogOpen(true);
  };
  
  // Currency conversion function
  const convertPrice = (price, currency) => {
    if (!price || isNaN(parseFloat(price))) return { price: 0, currency: 'EUR' };
    
    const numericPrice = parseFloat(price);
    
    if (convertToEUR) {
      if (currency === 'RON') {
        return {
          price: (numericPrice / 5.09).toFixed(2),
          currency: 'EUR'
        };
      } else if (currency === 'GBP') {
        return {
          price: (numericPrice * 1.15).toFixed(2),
          currency: 'EUR'
        };
      }
    }
    
    return {
      price: numericPrice.toFixed(2),
      currency: currency || 'EUR'
    };
  };
  
  // Product deduplication function - keeps the product with the latest date
  const deduplicateProducts = (products) => {
    const productMap = new Map();
    
    products.forEach(product => {
      // Create a more comprehensive unique key based on multiple product attributes
      const title = (product.title || '').toLowerCase().trim();
      const brand = (product.brand || '').toLowerCase().trim();
      const retailer = (product.retailer || '').toLowerCase().trim();
      const category = (product.clean_category || '').toLowerCase().trim();
      
      // Create unique key combining multiple attributes for better uniqueness
      const key = `${title}_${brand}_${retailer}_${category}`;
      
      if (!productMap.has(key)) {
        productMap.set(key, product);
      } else {
        // Compare dates and keep the one with the latest date
        const existingProduct = productMap.get(key);
        const existingDate = new Date(existingProduct.updated_at || existingProduct.date || 0);
        const currentDate = new Date(product.updated_at || product.date || 0);
        
        if (currentDate > existingDate) {
          productMap.set(key, product);
        }
      }
    });
    
    return Array.from(productMap.values());
  };
  
  const renderBrandComparisonLayout = () => {
    // First, deduplicate products - keep the one with the latest date
    const deduplicatedData = deduplicateProducts(filteredData);
    
    // Log deduplication results for debugging
    console.log(`Deduplication: ${filteredData.length} total records → ${deduplicatedData.length} unique products`);
    
    // Create price-aligned layout
    const priceAlignedLayout = createPriceAlignedLayout(deduplicatedData);
    
    return renderPriceAlignedBrands(priceAlignedLayout);
  };
  
  // Create price-aligned layout where products with similar prices are at the same height
  const createPriceAlignedLayout = (products) => {
    // Convert all prices to EUR for consistent comparison
    const productsWithConvertedPrices = products.map(product => {
      const priceData = convertPrice(product.actual_price || product.regular_price, product.currency);
      return {
        ...product,
        convertedPrice: parseFloat(priceData.price),
        displayPrice: priceData.price,
        displayCurrency: priceData.currency
      };
    });
    
    // Group products by price ranges (with tolerance for similar prices)
    const priceTolerance = 2; // EUR tolerance for grouping similar prices
    const priceGroups = [];
    
    // Sort all products by price first
    productsWithConvertedPrices.sort((a, b) => a.convertedPrice - b.convertedPrice);
    
    productsWithConvertedPrices.forEach(product => {
      // Find existing price group within tolerance
      let addedToGroup = false;
      for (let group of priceGroups) {
        const groupAvgPrice = group.products.reduce((sum, p) => sum + p.convertedPrice, 0) / group.products.length;
        if (Math.abs(product.convertedPrice - groupAvgPrice) <= priceTolerance) {
          group.products.push(product);
          addedToGroup = true;
          break;
        }
      }
      
      // Create new price group if no suitable group found
      if (!addedToGroup) {
        priceGroups.push({
          priceRange: product.convertedPrice,
          products: [product]
        });
      }
    });
    
    // Sort price groups by average price
    priceGroups.sort((a, b) => a.priceRange - b.priceRange);
    
    return priceGroups;
  };
  
  // Render price-aligned brand columns
  const renderPriceAlignedBrands = (priceGroups) => {
    // Get all unique brands
    const allBrands = [...new Set(priceGroups.flatMap(group => group.products.map(p => p.brand)))];
    
    if (allBrands.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No products found matching your criteria
          </Typography>
        </Box>
      );
    }

    // Calculate consistent heights for each price group across all brands
    const priceGroupHeights = priceGroups.map(priceGroup => {
      const maxProductsInGroup = Math.max(...allBrands.map(brand => 
        priceGroup.products.filter(p => p.brand === brand).length
      ));
      // Base height + spacing for stacked products (40px spacing)
      return maxProductsInGroup > 1 ? 120 + (maxProductsInGroup * 40) : 120;
    });

    return (
      <Box sx={{ display: 'flex', gap: 3, overflowX: 'auto' }}>
        {allBrands.map(brand => (
          <Box key={brand} sx={{ minWidth: 300, flex: 1 }}>
            {/* Brand Header */}
            <Paper 
              sx={{ 
                p: 2, 
                mb: 2, 
                textAlign: 'center',
                backgroundColor: brand === 'IQOS' ? '#2e7d32' : 
                               brand === 'Glo' ? '#4caf50' : 
                               brand === 'Ploom' ? '#1976d2' : '#666',
                color: 'white'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {brand}
              </Typography>
              <Typography variant="body2">
                {priceGroups.flatMap(group => group.products.filter(p => p.brand === brand)).length} products
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                (unique, latest data)
              </Typography>
            </Paper>

            {/* Products List - Price Aligned */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {priceGroups.map((priceGroup, groupIndex) => {
                const brandProducts = priceGroup.products.filter(p => p.brand === brand);
                const consistentHeight = priceGroupHeights[groupIndex]; // Use consistent height for all brands
                
                if (brandProducts.length === 0) {
                  // Empty space for this price range
                  return (
                    <Box key={`empty-${groupIndex}`} sx={{ position: 'relative', height: consistentHeight }}>
                      {/* Price Range Header */}
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary', 
                          fontWeight: 'bold',
                          textAlign: 'center',
                          mb: 1
                        }}
                      >
                        ~{priceGroup.priceRange.toFixed(2)} {priceGroup.products[0]?.displayCurrency || 'EUR'}
                      </Typography>
                      
                      <Box 
                        sx={{ 
                          height: consistentHeight - 40, // Account for header space
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          border: '1px dashed #e0e0e0',
                          borderRadius: 1,
                          backgroundColor: '#fafafa'
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No products in this price range
                        </Typography>
                      </Box>
                    </Box>
                  );
                }
                
                return (
                  <Box key={`group-${groupIndex}`} sx={{ position: 'relative', height: consistentHeight }}>
                    {/* Price Range Header */}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 'bold',
                        textAlign: 'center',
                        mb: 1,
                        position: 'relative',
                        zIndex: 10
                      }}
                    >
                      ~{priceGroup.priceRange.toFixed(2)} {priceGroup.products[0]?.displayCurrency || 'EUR'}
                      {brandProducts.length > 1 && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                          <Typography component="span" sx={{ color: 'primary.main', mr: 0.5 }}>
                            ({brandProducts.length} products)
                          </Typography>
                          <Box 
                            sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              backgroundColor: 'primary.main',
                              animation: 'pulse 2s infinite'
                            }} 
                          />
                        </Box>
                      )}
                    </Typography>
                    
                    {/* Stacked Products Container */}
                    <Box sx={{ 
                      position: 'relative', 
                      height: consistentHeight - 40, // Account for header space
                      minHeight: 80,
                      width: '100%'
                    }}>
                      {brandProducts.map((item, index) => {
                        const hasImage = item.image && item.image !== 'null' && item.image !== '';
                        const price = item.displayPrice;
                        const currency = item.displayCurrency;
                        const regularPrice = item.regular_price;
                        const convertedRegularPrice = convertPrice(regularPrice, item.currency);
                        const isOnSale = regularPrice && price && convertedRegularPrice.price !== price;
                        const isStacked = brandProducts.length > 1;
                        
                        // Stack from bottom up with first product in front
                        const stackOffsetY = (brandProducts.length - 1 - index) * 40; // Reverse Y offset (bottom to top) - 40px spacing
                        const stackOffsetX = index * 10; // X offset (right direction) - adjusted for 4:1 ratio
                        const stackZIndex = brandProducts.length - index; // First product (index 0) has highest z-index (in front)

                        return (
                           <Card 
                             key={index} 
                             onClick={() => handleProductClick(item)}
                             sx={{ 
                               position: 'absolute',
                               top: stackOffsetY,
                               left: stackOffsetX,
                               right: 0,
                               zIndex: stackZIndex,
                               display: 'flex', 
                               alignItems: 'center', 
                               p: 2,
                               cursor: 'pointer',
                               border: '2px solid #e0e0e0',
                               transition: 'all 0.3s ease-in-out',
                               '&:hover': { 
                                 zIndex: 1000, // Bring to front on hover
                                 boxShadow: 4,
                                 transform: 'translateY(-4px) scale(1.02)',
                                 backgroundColor: 'background.paper',
                                 border: '2px solid #1976d2'
                               }
                             }}
                           >
                            {/* Product Image */}
                            <Box 
                              sx={{ 
                                width: 80, 
                                height: 80, 
                                mr: 2,
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                borderRadius: 1,
                                cursor: hasImage ? 'pointer' : 'default',
                                flexShrink: 0
                              }}
                              onClick={() => hasImage && handleImageClick(item.image)}
                            >
                              {hasImage ? (
                                <img 
                                  src={item.image} 
                                  alt={item.title}
                                  style={{ 
                                    maxWidth: '100%', 
                                    maxHeight: '100%', 
                                    objectFit: 'contain' 
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <Box 
                                sx={{ 
                                  display: hasImage ? 'none' : 'flex', 
                                  flexDirection: 'column', 
                                  alignItems: 'center',
                                  color: '#666'
                                }}
                              >
                                <ImageIcon sx={{ fontSize: 24 }} />
                              </Box>
                            </Box>

                            {/* Product Info */}
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  fontWeight: 'bold', 
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {item.title || 'Untitled Product'}
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {item.retailer || 'Unknown Retailer'}
                              </Typography>

                               {/* Price */}
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                 <Typography 
                                   variant="h6" 
                                   color="primary" 
                                   sx={{ fontWeight: 'bold' }}
                                 >
                                   {price} {currency}
                                 </Typography>
                                 {isOnSale && convertedRegularPrice.price !== price && (
                                   <Typography 
                                     variant="body2" 
                                     color="text.secondary" 
                                     sx={{ textDecoration: 'line-through' }}
                                   >
                                     {convertPrice(regularPrice, item.currency).price} {convertPrice(regularPrice, item.currency).currency}
                                   </Typography>
                                 )}
                               </Box>

                              {/* Stock Status */}
                              {item.stock_status && (
                                <Chip 
                                  label={item.stock_status} 
                                  size="small"
                                  color={item.stock_status === 'In Stock' ? 'success' : 'error'}
                                  sx={{ mt: 0.5 }}
                                />
                              )}
                            </Box>
                          </Card>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };
  
  const renderFilters = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <FilterList sx={{ mr: 1 }} />
        Filters
      </Typography>
      
      <Grid container spacing={2}>
        {/* Search */}
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Search Products"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        {/* Country */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Country</InputLabel>
            <Select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              {filterOptions.countries.map(country => (
                <MenuItem key={country} value={country}>
                  {country.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Retailers */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Retailers</InputLabel>
            <Select
              multiple
              value={selectedRetailers}
              onChange={(e) => setSelectedRetailers(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {filterOptions.retailers.map(retailer => (
                <MenuItem key={retailer} value={retailer}>
                  {retailer}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Brands */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Brands</InputLabel>
            <Select
              multiple
              value={selectedBrands}
              onChange={(e) => setSelectedBrands(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {filterOptions.brands.map(brand => (
                <MenuItem key={brand} value={brand}>
                  {brand}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Categories */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Categories</InputLabel>
            <Select
              multiple
              value={selectedCategories}
              onChange={(e) => setSelectedCategories(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {filterOptions.categories.map(category => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Promo Types */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Promo Types</InputLabel>
            <Select
              multiple
              value={selectedPromoTypes}
              onChange={(e) => setSelectedPromoTypes(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {filterOptions.promoTypes.map(promoType => (
                <MenuItem key={promoType} value={promoType}>
                  {promoType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Variants (for Sticks) */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Variants</InputLabel>
            <Select
              multiple
              value={selectedVariants}
              onChange={(e) => setSelectedVariants(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {filterOptions.variants.map(variant => (
                <MenuItem key={variant} value={variant}>
                  {variant}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Intensities (for Sticks) */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Intensities</InputLabel>
            <Select
              multiple
              value={selectedIntensities}
              onChange={(e) => setSelectedIntensities(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {filterOptions.intensities.map(intensity => (
                <MenuItem key={intensity} value={intensity}>
                  {intensity}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Date Range */}
        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <DatePicker
                label="Start Date"
                value={dateRange.start}
                onChange={(newValue) => setDateRange(prev => ({ ...prev, start: newValue }))}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="End Date"
                value={dateRange.end}
                onChange={(newValue) => setDateRange(prev => ({ ...prev, end: newValue }))}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Box>
          </LocalizationProvider>
        </Grid>
        
        {/* Currency Conversion */}
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={convertToEUR}
                  onChange={(e) => setConvertToEUR(e.target.checked)}
                  color="primary"
                />
              }
               label={
                 <Box>
                   <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                     Convert to EUR
                   </Typography>
                   <Typography variant="caption" color="text.secondary">
                     1 EUR = 5.09 RON, 1 GBP = 1.15 EUR
                   </Typography>
                 </Box>
               }
            />
          </Box>
        </Grid>
        
        {/* Clear Filters */}
        <Grid item xs={12} md={3}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              setSelectedRetailers([]);
              setSelectedBrands([]);
              setSelectedCategories([]);
               setSelectedPromoTypes([]);
               setSelectedVariants([]);
               setSelectedIntensities([]);
               setSearchTerm('');
               setConvertToEUR(true);
               setDateRange({
                start: dayjs().subtract(30, 'days'),
                end: dayjs()
              });
            }}
          >
            Clear Filters
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading brand board data...</Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={loadData} sx={{ mt: 2 }}>
          <Refresh sx={{ mr: 1 }} />
          Retry
        </Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Add CSS animations */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
          <Store sx={{ mr: 2 }} />
          Brand Board
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Side-by-side product and brand comparison at aligned price points
        </Typography>
      </Box>
      
      {/* Filters */}
      {renderFilters()}
      
      {/* Segmentation Tabs */}
      <Paper sx={{ mb: 3 }}>
         <Tabs 
           value={activeTab} 
           onChange={handleTabChange}
           sx={{ borderBottom: 1, borderColor: 'divider' }}
         >
           <Tab 
             label={
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <Public sx={{ mr: 1 }} />
                 All
                 <Badge 
                   badgeContent={data.length} 
                   color="primary" 
                   sx={{ ml: 1 }}
                 />
               </Box>
             } 
           />
           <Tab 
             label={
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <DeviceHub sx={{ mr: 1 }} />
                 Devices
                 <Badge 
                   badgeContent={data.filter(item => 
                     item.clean_category === 'Devices' || 
                     (item.title && item.title.toLowerCase().includes('device'))
                   ).length} 
                   color="primary" 
                   sx={{ ml: 1 }}
                 />
               </Box>
             } 
           />
           <Tab 
             label={
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <LocalFireDepartment sx={{ mr: 1 }} />
                 Sticks
                 <Badge 
                   badgeContent={data.filter(item => 
                     item.clean_category === 'Consumables' || 
                     (item.title && (
                       item.title.toLowerCase().includes('stick') ||
                       item.title.toLowerCase().includes('terea') ||
                       item.title.toLowerCase().includes('levia') ||
                       item.title.toLowerCase().includes('delia') ||
                       item.title.toLowerCase().includes('fiit')
                     ))
                   ).length} 
                   color="primary" 
                   sx={{ ml: 1 }}
                 />
               </Box>
             } 
           />
           <Tab 
             label={
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <LocalOffer sx={{ mr: 1 }} />
                 Promo Offers
                 <Badge 
                   badgeContent={data.filter(item => 
                     item.clean_category === 'Offers' || 
                     item.clean_category === 'Starter Kits' ||
                     (item.title && (
                       item.title.toLowerCase().includes('offer') ||
                       item.title.toLowerCase().includes('promo') ||
                       item.title.toLowerCase().includes('bundle') ||
                       item.title.toLowerCase().includes('kit')
                     ))
                   ).length} 
                   color="primary" 
                   sx={{ ml: 1 }}
                 />
               </Box>
             } 
           />
         </Tabs>
      </Paper>
      
      {/* Results Summary */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">
            Brand Comparison - {deduplicateProducts(filteredData).length} unique products across {[...new Set(filteredData.map(item => item.brand))].length} brands
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredData.length > 0 && (
              <>
                Showing {deduplicateProducts(filteredData).length} unique products 
                (from {filteredData.length} total records)
              </>
            )}
          </Typography>
        </Box>
        <Button onClick={loadData} startIcon={<Refresh />}>
          Refresh Data
        </Button>
      </Box>
      
      {/* Brand Comparison Layout */}
      {renderBrandComparisonLayout()}
      
       {/* Image Preview Dialog */}
       <Dialog 
         open={imageDialogOpen} 
         onClose={() => setImageDialogOpen(false)}
         maxWidth="md"
         fullWidth
       >
         <DialogTitle>Product Image</DialogTitle>
         <DialogContent>
           {selectedImage && (
             <img 
               src={selectedImage} 
               alt="Product" 
               style={{ width: '100%', height: 'auto' }}
             />
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setImageDialogOpen(false)}>Close</Button>
         </DialogActions>
       </Dialog>
       
       {/* Product Detail Dialog */}
       <Dialog 
         open={productDetailDialogOpen} 
         onClose={() => setProductDetailDialogOpen(false)}
         maxWidth="lg"
         fullWidth
       >
         <DialogTitle>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
             <Store sx={{ fontSize: 24 }} />
             Product Details
           </Box>
         </DialogTitle>
         <DialogContent>
           {selectedProduct && (
             <Box>
               <Grid container spacing={3}>
                 {/* Product Image */}
                 <Grid item xs={12} md={4}>
                   <Card>
                     <CardContent>
                       <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <ImageIcon sx={{ fontSize: 20 }} />
                         Product Image
                       </Typography>
                       <Box 
                         component="img"
                         src={selectedProduct.image && selectedProduct.image !== 'null' ? selectedProduct.image : 'https://via.placeholder.com/300x200?text=Product+Image'}
                         alt={selectedProduct.title}
                         sx={{
                           width: '100%',
                           height: 300,
                           objectFit: 'cover',
                           borderRadius: 1,
                           border: '1px solid #e0e0e0'
                         }}
                       />
                     </CardContent>
                   </Card>
                 </Grid>

                 {/* Product Information */}
                 <Grid item xs={12} md={8}>
                   <Card>
                     <CardContent>
                       <Typography variant="h5" gutterBottom sx={{ color: '#1976d2' }}>
                         {selectedProduct.title}
                       </Typography>
                       
                       <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                         <Chip 
                           label={selectedProduct.brand}
                           color="primary" 
                           variant="outlined"
                         />
                         <Chip 
                           label={selectedProduct.country}
                           color="secondary" 
                           variant="outlined"
                         />
                         <Chip 
                           label={selectedProduct.retailer}
                           color="success" 
                           variant="outlined"
                         />
                       </Box>

                       <Divider sx={{ my: 2 }} />

                       {/* Product Characteristics */}
                       <Typography variant="h6" gutterBottom>
                         Product Characteristics
                       </Typography>
                       <Grid container spacing={2} sx={{ mb: 2 }}>
                         <Grid item xs={6} md={3}>
                           <Typography variant="body2">
                             <strong>Category:</strong> {selectedProduct.clean_category || 'N/A'}
                           </Typography>
                         </Grid>
                         <Grid item xs={6} md={3}>
                           <Typography variant="body2">
                             <strong>Flavor:</strong> {selectedProduct.flavor_intensity || 'N/A'}
                           </Typography>
                         </Grid>
                         <Grid item xs={6} md={3}>
                           <Typography variant="body2">
                             <strong>Strength:</strong> {selectedProduct.intensity || 'N/A'}
                           </Typography>
                         </Grid>
                         <Grid item xs={6} md={3}>
                           <Typography variant="body2">
                             <strong>Variant:</strong> {selectedProduct.variant || 'N/A'}
                           </Typography>
                         </Grid>
                       </Grid>

                       <Divider sx={{ my: 2 }} />

                       {/* Pricing Information */}
                       <Typography variant="h6" gutterBottom>
                         Pricing & Availability
                       </Typography>
                       <Grid container spacing={2}>
                         <Grid item xs={6} md={3}>
                           <Typography variant="body2">
                             <strong>Currency:</strong> {selectedProduct.currency || 'N/A'}
                           </Typography>
                         </Grid>
                         <Grid item xs={6} md={3}>
                           <Typography variant="body2">
                             <strong>Actual Price:</strong> {selectedProduct.actual_price || 'N/A'}
                           </Typography>
                         </Grid>
                         <Grid item xs={6} md={3}>
                           <Typography variant="body2">
                             <strong>Regular Price:</strong> {selectedProduct.regular_price || 'N/A'}
                           </Typography>
                         </Grid>
                         <Grid item xs={6} md={3}>
                           <Typography variant="body2">
                             <strong>Stock:</strong> {selectedProduct.stock_status || 'N/A'}
                           </Typography>
                         </Grid>
                       </Grid>

                       <Divider sx={{ my: 2 }} />

                       {/* Additional Information */}
                       <Typography variant="h6" gutterBottom>
                         Additional Information
                       </Typography>
                       <Grid container spacing={2}>
                         <Grid item xs={12} md={6}>
                           <Typography variant="body2">
                             <strong>Product ID:</strong> {selectedProduct.id || 'N/A'}
                           </Typography>
                         </Grid>
                         <Grid item xs={12} md={6}>
                           <Typography variant="body2">
                             <strong>Position:</strong> {selectedProduct.position || 'N/A'}
                           </Typography>
                         </Grid>
                         <Grid item xs={12}>
                           <Typography variant="body2">
                             <strong>URL:</strong> 
                             {selectedProduct.url ? (
                               <a 
                                 href={selectedProduct.url} 
                                 target="_blank" 
                                 rel="noopener noreferrer"
                                 style={{ marginLeft: 8, color: '#1976d2' }}
                               >
                                 View Product
                               </a>
                             ) : ' N/A'}
                           </Typography>
                         </Grid>
                         <Grid item xs={12}>
                           <Typography variant="body2">
                             <strong>Last Updated:</strong> {selectedProduct.updated_at || 'N/A'}
                           </Typography>
                         </Grid>
                       </Grid>
                     </CardContent>
                   </Card>
                 </Grid>
               </Grid>
             </Box>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setProductDetailDialogOpen(false)}>Close</Button>
         </DialogActions>
       </Dialog>
    </Box>
  );
}

export default BrandBoardView;
